import { useState } from "react";
import {
  Checkbox,
  Divider,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import { DragIndicator, RestartAlt, ViewColumn } from "@mui/icons-material";
import DataGridButton from "../../components/DataGridButton";
import { tokens } from "../../theme";
import {
  dataGridButtonStyles,
  envListButtonStyles,
} from "../../styles/buttonStyles";

// Lets the user hide/show data columns in the Environments table, and drag
// rows here to reorder those same columns in the table (the drag handle
// drives `onReorder`, which the caller feeds into the same order state that
// dragging the table's own column headers updates). Renders its own menu
// instead of DataGridPro's built-in "Manage columns" panel, since that panel
// ships unstyled MUI-default chrome rather than this app's own tokens (the
// same gap that made stock MUI panels render white in dark mode elsewhere).
// `columns` are the toggleable ones only, already in current table order
// (structural columns like "details"/"actions"/"name" are filtered out by
// the caller); `value` is a DataGridPro columnVisibilityModel
// ({ [field]: boolean }, missing = visible).
const ColumnVisibilityMenu = ({
  columns,
  value,
  onChange,
  onReorder,
  onReset,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [draggedField, setDraggedField] = useState(null);

  const toggleColumn = (field) => {
    const isVisible = value[field] !== false;
    onChange({ ...value, [field]: !isVisible });
  };

  const handleDrop = (targetField) => {
    if (!draggedField || draggedField === targetField) {
      setDraggedField(null);
      return;
    }
    const fields = columns.map((c) => c.field);
    const from = fields.indexOf(draggedField);
    const to = fields.indexOf(targetField);
    fields.splice(from, 1);
    fields.splice(to, 0, draggedField);
    onReorder(fields);
    setDraggedField(null);
  };

  const resetAndClose = () => {
    onReset();
    setAnchorEl(null);
  };

  return (
    <>
      <DataGridButton
        sx={{
          ...dataGridButtonStyles.pageTopButtons,
          ...envListButtonStyles(colors).toolbarOutlined,
        }}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        toolTipProps={{ title: "Choose visible columns" }}
      >
        <ViewColumn sx={{ fontSize: 16 }} />
        <Typography>COLUMNS</Typography>
      </DataGridButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {columns.map(({ field, label }) => {
          const checked = value[field] !== false;
          return (
            <MenuItem
              key={field}
              dense
              draggable
              onDragStart={() => setDraggedField(field)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => handleDrop(field)}
              onDragEnd={() => setDraggedField(null)}
              onClick={() => toggleColumn(field)}
              sx={{
                cursor: "grab",
                opacity: draggedField === field ? 0.4 : 1,
              }}
            >
              <DragIndicator
                fontSize="small"
                sx={{ mr: 0.5, color: colors.muted }}
              />
              <Checkbox checked={checked} size="small" disableRipple />
              {label}
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem dense onClick={resetAndClose}>
          <RestartAlt fontSize="small" sx={{ mr: 0.75, color: colors.muted }} />
          Reset to default
        </MenuItem>
      </Menu>
    </>
  );
};

export default ColumnVisibilityMenu;
